import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, Check, AlertTriangle } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAX_BYTES = 5 * 1024 * 1024;

const FIELD_BASE =
  "w-full bg-transparent border-b border-ink/20 px-0 py-3 text-ink placeholder:text-ink/30 focus:outline-none focus:border-ink transition-colors font-sans text-[15px]";
const LABEL_BASE =
  "block text-[11px] font-mono tracking-[0.22em] uppercase text-ink-soft mb-1";

export const ApplicationForm = ({ role, onClose }) => {
  const dialogRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverMessage, setServerMessage] = useState(null); // {type, text}
  const [done, setDone] = useState(false);
  const [resumeName, setResumeName] = useState("");
  const [errors, setErrors] = useState({});
  // Timestamp captured on first render — used server-side to reject bot-speed submissions.
  const openedAtRef = useRef(Date.now() / 1000);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setResumeName("");
      return;
    }
    if (file.type !== "application/pdf") {
      setErrors((p) => ({ ...p, resume: "Resume must be a PDF file." }));
      e.target.value = "";
      setResumeName("");
      return;
    }
    if (file.size > MAX_BYTES) {
      setErrors((p) => ({ ...p, resume: "Resume must be 5 MB or smaller." }));
      e.target.value = "";
      setResumeName("");
      return;
    }
    setErrors((p) => ({ ...p, resume: undefined }));
    setResumeName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setServerMessage(null);

    const fd = new FormData(e.target);
    // Ensure role title is always populated (modal always supplies it)
    if (!fd.get("role_title")) fd.set("role_title", role?.title || "General Application");
    if (role?.id) fd.set("role_id", role.id);
    // Anti-abuse: send the ts when the form was opened so backend can enforce a min-fill delay.
    fd.set("form_opened_at", String(openedAtRef.current));

    if (!fd.get("resume") || !fd.get("resume").size) {
      setErrors((p) => ({ ...p, resume: "Please attach your resume." }));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/careers/apply`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail =
          typeof data.detail === "string"
            ? data.detail
            : "Something went wrong. Please try again.";
        setServerMessage({ type: "error", text: detail });
      } else {
        setDone(true);
        setServerMessage({
          type: "success",
          text: `Thanks ${data.full_name?.split(" ")[0] || ""}, we've received your application. Reference: ${data.id?.slice(0, 8)}.`,
        });
      }
    } catch (err) {
      setServerMessage({
        type: "error",
        text: "Network error. Check your connection and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[80] bg-ink/70 backdrop-blur-sm flex items-stretch md:items-center justify-center md:p-8"
        onClick={onClose}
        data-testid="application-modal-overlay"
      >
        <motion.div
          ref={dialogRef}
          initial={{ opacity: 0, y: 40, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          data-testid="application-modal"
          className="relative w-full md:max-w-3xl bg-bone shadow-2xl overflow-hidden md:rounded-sm max-h-screen md:max-h-[92vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 p-6 md:p-8 border-b border-ink/10 bg-white shrink-0">
            <div>
              <div className="overline text-amber-accent mb-2">Application</div>
              <div className="font-display font-medium text-ink text-xl md:text-2xl leading-tight">
                {role?.title || "General Application"}
              </div>
              {(role?.location || role?.department) && (
                <div className="mt-1 text-[12px] text-ink-soft">
                  {[role?.location, role?.department, role?.experience]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="application-modal-close"
              className="w-9 h-9 grid place-items-center text-ink hover:bg-ink/5 rounded-sm"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="overflow-y-auto flex-1">
            {done ? (
              <div className="p-8 md:p-12 text-center" data-testid="application-success">
                <div className="w-14 h-14 rounded-full bg-forest-accent/10 text-forest-accent grid place-items-center mx-auto">
                  <Check className="w-7 h-7" />
                </div>
                <div className="mt-6 font-display text-2xl md:text-3xl text-ink leading-tight">
                  Application received.
                </div>
                <p className="mt-3 text-ink-soft max-w-md mx-auto leading-relaxed">
                  {serverMessage?.text}
                </p>
                <p className="mt-2 text-ink-soft text-sm">
                  Our team usually replies within one business day.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-8 inline-flex items-center gap-1.5 border border-ink text-ink px-5 py-2.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-bone transition-all"
                >
                  Close
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                encType="multipart/form-data"
                className="p-6 md:p-8 space-y-7"
                data-testid="application-form"
              >
                {/* Hidden: role meta */}
                <input type="hidden" name="role_title" defaultValue={role?.title || ""} />
                <input type="hidden" name="role_id" defaultValue={role?.id || ""} />

                {/* Honeypot — hidden from humans, catches bots that fill every field.
                    Do NOT rename the field to something obviously security-y like
                    "honeypot": bots test for common patterns. "website" is
                    innocuous and doesn't tempt real users to click.
                */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    top: "auto",
                    width: "1px",
                    height: "1px",
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="website">
                    Website (leave this field empty)
                  </label>
                  <input
                    id="website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    defaultValue=""
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                  <div className="sm:col-span-2">
                    <label htmlFor="full_name" className={LABEL_BASE}>Full name *</label>
                    <input
                      id="full_name"
                      name="full_name"
                      required
                      minLength={2}
                      maxLength={120}
                      autoComplete="name"
                      data-testid="form-full-name"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className={LABEL_BASE}>Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      data-testid="form-email"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className={LABEL_BASE}>Phone *</label>
                    <input
                      id="phone"
                      name="phone"
                      required
                      autoComplete="tel"
                      data-testid="form-phone"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="years_experience" className={LABEL_BASE}>
                      Years of experience *
                    </label>
                    <input
                      id="years_experience"
                      name="years_experience"
                      type="number"
                      min="0"
                      max="60"
                      step="0.5"
                      required
                      data-testid="form-years"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="current_company" className={LABEL_BASE}>
                      Current company
                    </label>
                    <input
                      id="current_company"
                      name="current_company"
                      data-testid="form-company"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="linkedin_url" className={LABEL_BASE}>
                      LinkedIn URL
                    </label>
                    <input
                      id="linkedin_url"
                      name="linkedin_url"
                      type="url"
                      placeholder="https://linkedin.com/in/…"
                      data-testid="form-linkedin"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="expected_ctc" className={LABEL_BASE}>
                      Expected CTC
                    </label>
                    <input
                      id="expected_ctc"
                      name="expected_ctc"
                      placeholder="e.g. ₹18 LPA"
                      data-testid="form-ctc"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div>
                    <label htmlFor="notice_period" className={LABEL_BASE}>
                      Notice period
                    </label>
                    <input
                      id="notice_period"
                      name="notice_period"
                      placeholder="e.g. 30 days"
                      data-testid="form-notice"
                      className={FIELD_BASE}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cover_note" className={LABEL_BASE}>
                      Why Milestone, and why this role? *
                    </label>
                    <textarea
                      id="cover_note"
                      name="cover_note"
                      required
                      rows={4}
                      minLength={10}
                      maxLength={4000}
                      data-testid="form-cover"
                      className={`${FIELD_BASE} resize-y`}
                    />
                  </div>

                  {/* Resume uploader */}
                  <div className="sm:col-span-2">
                    <label className={LABEL_BASE}>Resume (PDF, max 5 MB) *</label>
                    <label
                      htmlFor="resume"
                      className="mt-1 flex items-center justify-between gap-4 border border-dashed border-ink/25 p-4 cursor-pointer hover:border-ink/50 hover:bg-white transition-colors"
                    >
                      <span className="inline-flex items-center gap-3 text-ink">
                        <span className="w-9 h-9 grid place-items-center bg-ink/5 rounded-sm">
                          <Upload className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                        <span className="text-sm">
                          {resumeName || "Click to attach resume"}
                        </span>
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-soft">
                        PDF · ≤ 5 MB
                      </span>
                    </label>
                    <input
                      id="resume"
                      name="resume"
                      type="file"
                      accept="application/pdf,.pdf"
                      onChange={handleFile}
                      data-testid="form-resume"
                      className="sr-only"
                    />
                    {errors.resume && (
                      <div className="mt-2 text-xs text-red-700" data-testid="form-resume-error">
                        {errors.resume}
                      </div>
                    )}
                  </div>
                </div>

                {serverMessage && serverMessage.type === "error" && (
                  <div
                    data-testid="form-server-error"
                    className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 p-3"
                  >
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span>{serverMessage.text}</span>
                  </div>
                )}

                <div className="pt-2 flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="form-submit"
                    className="inline-flex items-center gap-2 bg-ink text-bone px-6 py-3 text-sm font-medium rounded-sm hover:bg-ink/85 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      "Submit application"
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm text-ink-soft hover:text-ink transition-colors"
                  >
                    Cancel
                  </button>
                  <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-ink-soft ml-auto">
                    Your data goes only to our hiring team.
                  </span>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
